import React from 'react'
import { FormControlLabel, Box, Typography, Radio, RadioGroup } from '@mui/material'
import { withStyles } from '@mui/styles'
import { cardRadioButtonStyles } from './card-radio-button.styles'

/**
 * @typedef {object} RadioOption
 * @property {string | number} key - El valor único que se devolverá cuando se seleccione esta opción.
 * @property {string} value - El texto legible que se mostrará en la tarjeta de la opción.
 */

/**
 * @typedef {object} ChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop `name`.
 * @property {string | number} target.value - El `key` de la opción seleccionada.
 */

/**
 * @typedef {object} CardRadioButtonProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {RadioOption[]} [values=[]] - Un array de objetos que definen las opciones a renderizar.
 * @property {string | number} [value] - El `key` de la opción actualmente seleccionada, para operar como un componente controlado.
 * @property {function(ChangeEvent): void} onChange - La función callback que se ejecuta cuando el valor cambia.
 * @property {string} name - El nombre del grupo de radio, que se incluirá en el objeto de evento sintético del `onChange`.
 * @property {string} [title] - Un título opcional que se muestra encima del grupo de opciones.
 * @property {'small' | 'normal'} [fontSize] - Controla el tamaño de la fuente del título. Si es 'small', el título será más pequeño.
 */

/**
 * Un componente de selección que renderiza un grupo de opciones de radio (`RadioGroup`)
 * donde cada opción se presenta visualmente como una tarjeta o un botón seleccionable.
 *
 * Está diseñado para ser un componente controlado, gestionando su estado a través de las
 * props `value` y `onChange`. Es ideal para situaciones donde se requiere una selección
 * visualmente más destacada que los radio buttons estándar.
 *
 * @component
 * @example
 * const options = [
 * { key: 'option1', value: 'Primera Opción' },
 * { key: 'option2', value: 'Segunda Opción' },
 * { key: 'option3', value: 'Tercera Opción' },
 * ];
 *
 * const MyForm = () => {
 * const [selection, setSelection] = useState('option2');
 *
 * const handleChange = (event) => {
 * console.log(`Campo: ${event.target.name}, Valor: ${event.target.value}`);
 * setSelection(event.target.value);
 * };
 *
 * return (
 * <CardRadioButton
 * title="Selecciona una alternativa"
 * name="alternatives"
 * values={options}
 * value={selection}
 * onChange={handleChange}
 * />
 * );
 * };
 *
 * @param {CardRadioButtonProps} props - Las props del componente CardRadioButton.
 */
export const CardRadioButton = React.memo(
  withStyles(cardRadioButtonStyles)(({ classes, values = [], ...props }) => {
    const value = props.value ? props.value : null
    const typographyVariant = props.fontSize === 'small' ? '2' : '1'
    const handleChange = (_, value) => {
      props.onChange({ target: { name: props.name, value } })
    }
    return (
      <Box>
        {props.title && (
          <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
            {props.title}
          </Typography>
        )}
        <Box display="flex" justifyContent="center">
          <RadioGroup
            value={value}
            style={{ display: 'flex', flexDirection: 'row' }}
            onChange={handleChange}
          >
            {values.map((element, i) => {
              return (
                <FormControlLabel
                  key={i}
                  className={classes.card + (element.key === value ? ' ' + classes.cardChosen : '')}
                  value={element.key}
                  control={<Radio className={classes.cardMarker} />}
                  label={element.value}
                />
              )
            })}
          </RadioGroup>
        </Box>
      </Box>
    )
  })
)
